import { isObject } from 'utils/clickme/typeGuards';

type Keys = (string | Keys)[];
type AwaitedReturn<T> = {
    // eslint-disable-next-line @typescript-eslint/ban-types
    [key in keyof T]: T[key] extends string | number | boolean | symbol | bigint | Date | Function
        ? T[key]
        : AwaitedReturn<Awaited<T[key]>>;
};

// ключ для идентификации массива при восстановлении начальной структуры переданной сущности
// необходимо понимать, что мы восстанавливаем
const ARRAY_KEY = '$$array$$';

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const getPromises = <T extends Record<string, any>>(requests: T) =>
    Object.entries(requests).reduce(
        (result, [key, value]) => {
            const isArray = Array.isArray(value);

            if (value instanceof Promise) {
                result.promises.push(value);
                result.keys.push(key);
            } else if (isObject(value) || isArray) {
                if (!(isArray ? value : Object.keys(value)).length) {
                    result.promises.push(value);
                    result.keys.push(key);
                } else {
                    const { promises, keys } = getPromises(value);

                    result.promises.push(...promises);
                    result.keys.push(key, isArray ? [ARRAY_KEY, ...keys] : keys);
                }
            } else {
                result.promises.push(value);
                result.keys.push(key);
            }

            return result;
        },
        { promises: [] as (Promise<unknown> | unknown)[], keys: [] as Keys }
    );

const computeResult = <T extends (Promise<unknown> | unknown | unknown[])[]>(
    promises: T,
    keys: Keys
): Record<keyof Keys, T[number]> => {
    const isArray = keys[0] === ARRAY_KEY;
    const result = (isArray ? [] : {}) as Record<keyof Keys, T[number]>;

    let keyIndex = isArray ? 1 : 0;

    while (promises.length && keys[keyIndex]) {
        const nextKey = keys[keyIndex + 1];
        const isNestingKeys = Array.isArray(nextKey);

        result[keys[keyIndex] as keyof Keys] = isNestingKeys ? computeResult(promises, nextKey) : promises.pop();

        keyIndex += isNestingKeys ? 2 : 1;
    }

    return result;
};

// не работает с Map/WeakMap/Set/промисами, вложенными в промисы { a: Promise.resolve({ b: Promise.resolve(5) }) }
// eslint-disable-next-line  @typescript-eslint/no-explicit-any, import/prefer-default-export
export const resolvePromisesMap = <T extends Record<string, any>>(requests: T): Promise<AwaitedReturn<T>> => {
    const { keys, promises } = getPromises(requests);

    // todo seems to need to add error handling (probably it could be promise allSettled flow)
    return Promise.all(promises).then((result) => computeResult(result.reverse(), keys) as unknown as AwaitedReturn<T>);
};
